// =====================================================================
//  LA NAVEGACIÓN  (D231, sesión 176)
// =====================================================================
//  Dos cosas que valen para TODAS las pantallas y por eso viven acá, una
//  sola vez (pedido de Steven, 3/11: «práctico, intuitivo y cómodo, en todo
//  el sistema»):
//
//  1 · LAS FICHAS. Lo que se abre con «Ver» (una orden, la ficha de un
//      proveedor, la cuenta de un cliente…) ocupa la pestaña entera, con
//      «← Volver a …» arriba, en vez de aparecer debajo de la lista y hacer
//      saltar la página. Es la regla de la ficha de la venta (D220),
//      generalizada:
//        · la pestaña muestra sólo la ficha: el resto se esconde por CSS
//          (`.con-ficha > :not(.en-ficha)`), así que lo que se agregue
//          mientras tanto también queda escondido;
//        · volver deja la lista donde estaba (la misma altura de la página)
//          y resalta un momento la fila de la que se salió;
//        · el «atrás» del celular o del navegador también vuelve: cada ficha
//          abierta es un paso en el historial del navegador;
//        · una ficha abierta desde otra (la orden desde la ficha de su
//          proveedor) se apila: volver lleva a la ficha anterior;
//        · cambiar de pestaña cierra las fichas (se llega a la lista).
//      El módulo de cada pantalla sigue siendo el dueño de su tarjeta: la
//      abre con `abrirComoFicha()`, y su limpieza (el «ABIERTA = null», el
//      turno) va en `alCerrar`, que corre al volver por cualquier camino.
//      Cerrar sólo esconde: lo tecleado en la lista sigue ahí (regla 6).
//
//  2 · PLEGAR LAS TARJETAS. El título de cada `.card` se toca y la tarjeta
//      queda sólo con el título (▸); se vuelve a tocar y se despliega (▾).
//      Lo plegado se recuerda en ese navegador, por pantalla y tarjeta. Arriba
//      de cada pantalla con dos tarjetas o más, «Plegar todas» / «Desplegar
//      todas». Una tarjeta plegada que recibe un aviso o un error nuevo se
//      despliega sola: lo que hay que ver no queda escondido. Plegar sólo
//      esconde: no relee nada ni borra lo tecleado.
//      Se arma solo al arrancar sobre todas las tarjetas de `index.html`: una
//      tarjeta nueva lo tiene sin tocar este archivo.

// ---------------------------------------------------------------------
//  1 · Las fichas
// ---------------------------------------------------------------------

type Ficha = {
  el: HTMLElement;
  seccion: HTMLElement;
  lista: string;          // adónde vuelve si es la primera: «las órdenes»
  nombre: string;         // cómo se la nombra si otra se abre encima: «la orden»
  alCerrar?: () => void;
  desde: Element | null;  // la fila (o el botón) desde la que se abrió
  altura: number;         // la altura de la página al abrirla
  boton: HTMLElement;     // su «← Volver a …»
};

let PILA: Ficha[] = [];

//  EL HISTORIAL DEL NAVEGADOR: un paso por ficha abierta, para que el
//  «atrás» vuelva a la lista. `history.go()` es ASÍNCRONO: si se lo llama y
//  enseguida se pone otro paso (cerrar la ficha del cliente y abrir la venta
//  en el mismo click), el viaje termina en otro lado y un «atrás» después
//  sale del sistema (lo encontró la prueba 73). Por eso las fichas no tocan
//  el historial: lo AJUSTA una sola función, después de cada tanda de
//  cambios, comparando cuántos pasos hay con cuántas fichas hay, y no da un
//  paso nuevo mientras un viaje está en camino.
//  La altura de la página la pone esta pantalla al volver: si la pusiera el
//  navegador, pondría la de la ficha (arriba de todo).
try { history.scrollRestoration = 'manual'; } catch { /* navegador viejo */ }
let PASOS = 0;            // los pasos nuestros que hay en el historial
let VIAJANDO = false;     // un `history.go` que todavía no llegó
let PROGRAMADO = false;
function ajustarHistorial(){
  PROGRAMADO = false;
  if (VIAJANDO) return;   // se retoma cuando llegue (en el `popstate`)
  const quiero = PILA.length;
  try {
    if (PASOS > quiero){
      VIAJANDO = true;
      const d = quiero - PASOS;
      PASOS = quiero;
      history.go(d);
      return;
    }
    while (PASOS < quiero){
      PASOS++;
      history.pushState({ nubilonFicha: PASOS }, '');
    }
  } catch { VIAJANDO = false; /* sin historial: los botones igual vuelven */ }
}
//  Se ajusta una vez por tanda (en un microtask) y no en cada cambio: un
//  «atrás» de varios pasos cierra varias fichas seguidas, y ajustar en el
//  medio daría pasos que enseguida habría que sacar.
function programarHistorial(){
  if (PROGRAMADO) return;
  PROGRAMADO = true;
  queueMicrotask(ajustarHistorial);
}
//  Lo último que se tocó: de ahí sale «la fila de la que se salió» sin que
//  cada pantalla tenga que pasarla.
let ULTIMO_TOQUE: Element | null = null;
document.addEventListener('click', e => { ULTIMO_TOQUE = e.target as Element; }, true);

const filaDe = (x: Element | null) =>
  x ? (x.closest('tr, li, .card, .parada') || x) : null;

//  Lo que se ve: la ficha de arriba de la pila y el camino que la lleva a su
//  pestaña. Todo lo demás de la pestaña, escondido por la regla del CSS.
function marcar(){
  document.querySelectorAll('.con-ficha').forEach(x => x.classList.remove('con-ficha'));
  document.querySelectorAll('.en-ficha').forEach(x => x.classList.remove('en-ficha'));
  const f = PILA[PILA.length - 1];
  if (!f) return;
  let x: HTMLElement | null = f.el;
  while (x && x !== f.seccion){
    x.classList.add('en-ficha');
    x.parentElement?.classList.add('con-ficha');
    x = x.parentElement;
  }
  for (const g of PILA) g.boton.textContent = textoVolver(g);
}

function textoVolver(f: Ficha){
  const i = PILA.indexOf(f);
  const antes = i > 0 ? PILA[i - 1] : null;
  return '← Volver a ' + (antes ? antes.nombre : f.lista);
}

//  La barra de arriba de la ficha, con su «← Volver a …». Una ficha que ya
//  tiene el suyo arriba (la del cliente, la de la venta) lo pasa en `boton`.
function barraDe(el: HTMLElement): HTMLElement {
  let b = el.querySelector(':scope > .ficha-barra > .ficha-volver') as HTMLElement | null;
  if (b) return b;
  const barra = document.createElement('div');
  barra.className = 'ficha-barra';
  const nuevo = document.createElement('button');
  nuevo.type = 'button';
  nuevo.className = 'b b-sec ficha-volver';
  barra.appendChild(nuevo);
  el.insertBefore(barra, el.firstChild);
  return nuevo;
}

export type OpcionesFicha = {
  //  Adónde se vuelve: «← Volver a las órdenes».
  lista: string;
  //  Cómo se la nombra si otra ficha se abre encima: «← Volver a la orden».
  nombre: string;
  //  La limpieza del módulo (el «abierta = null», el turno). Corre al volver
  //  por cualquier camino: el botón, el «atrás», cambiar de pestaña.
  alCerrar?: () => void;
  //  Si la ficha ya tiene su «← Volver» arriba.
  boton?: HTMLElement;
};

//  Abrir `el` como ficha. Si ya es la de arriba, no hace nada (las pantallas
//  vuelven a «abrir» la misma para releerla). Si estaba más abajo en la pila
//  (la orden → su proveedor → otra orden), pasa arriba.
export function abrirComoFicha(el: HTMLElement, o: OpcionesFicha){
  el.classList.remove('oculto', 'plegada');
  const seccion = el.closest('section') as HTMLElement | null;
  if (!seccion) return;
  const tope = PILA[PILA.length - 1];
  if (tope && tope.el === el){
    tope.alCerrar = o.alCerrar; tope.lista = o.lista; tope.nombre = o.nombre;
    marcar();
    return;
  }
  //  Una ficha de otra pestaña no se apila: quedó de antes.
  if (tope && tope.seccion !== seccion) cerrarFichas();
  const yaEstaba = PILA.findIndex(f => f.el === el);
  const vieja = yaEstaba >= 0 ? PILA.splice(yaEstaba, 1)[0] : null;
  const boton = o.boton || barraDe(el);
  boton.classList.add('ficha-volver');
  if (!boton.dataset.fichaLista){
    boton.dataset.fichaLista = '1';
    boton.addEventListener('click', () => volverDe(el));
  }
  const f: Ficha = {
    el, seccion, boton, lista: o.lista, nombre: o.nombre, alCerrar: o.alCerrar,
    //  Sólo si se tocó en esta misma pestaña (desde otra, no hay fila a la que volver).
    desde: vieja ? vieja.desde : (ULTIMO_TOQUE && seccion.contains(ULTIMO_TOQUE) ? filaDe(ULTIMO_TOQUE) : null),
    altura: vieja ? vieja.altura : window.scrollY,
  };
  PILA.push(f);
  marcar();
  programarHistorial();
  el.classList.remove('ficha-entra');
  void el.offsetWidth;                       // para que la animación vuelva a correr
  el.classList.add('ficha-entra');
  window.scrollTo({ top: 0 });
}

//  Sacar `f` de la pila, esconderla y correr la limpieza de su módulo.
function soltar(f: Ficha){
  PILA = PILA.filter(g => g !== f);
  f.el.classList.add('oculto');
  f.el.classList.remove('ficha-entra');
  try { f.alCerrar?.(); } catch (e) { console.error(e); }
}

//  Volver de la ficha `el`: la deja, y vuelve a lo que había debajo —la
//  ficha anterior o la lista— a la altura en que estaba, con la fila
//  resaltada.
function volverDe(el: HTMLElement){
  const f = PILA.find(g => g.el === el);
  if (!f){ el.classList.add('oculto'); return; }
  const eraTope = PILA[PILA.length - 1] === f;
  soltar(f);
  marcar();
  programarHistorial();
  if (!eraTope) return;
  window.scrollTo({ top: f.altura });
  const fila = f.desde;
  if (fila && fila.isConnected){
    fila.classList.remove('volvi');
    void (fila as HTMLElement).offsetWidth;
    fila.classList.add('volvi');
    setTimeout(() => fila.classList.remove('volvi'), 1600);
    const foco = (fila.matches('button, a') ? fila : fila.querySelector('button, a')) as HTMLElement | null;
    foco?.focus({ preventScroll: true });
  }
}

//  Lo que usa una pantalla cuando ELLA cierra su ficha (su botón «Cerrar»,
//  o después de un acto que la termina). Si no estaba abierta como ficha,
//  sólo la esconde.
export function cerrarComoFicha(el: HTMLElement){
  if (PILA.some(f => f.el === el)) volverDe(el);
  else el.classList.add('oculto');
}

//  Cambiar de pestaña: se llega a la lista. Sin mover la página ni resaltar.
export function cerrarFichas(){
  if (!PILA.length) return;
  for (const f of [...PILA].reverse()) soltar(f);
  marcar();
  programarHistorial();
}

export const hayFicha = (el: HTMLElement) => PILA.some(f => f.el === el);

//  El «atrás» (o el «adelante») del navegador. Lo que dice el paso al que se
//  llegó es cuántas fichas tiene que haber.
window.addEventListener('popstate', e => {
  if (VIAJANDO){ VIAJANDO = false; ajustarHistorial(); return; }
  const en = Number((e.state as any)?.nubilonFicha) || 0;
  if (en < PASOS){
    PASOS = en;
    //  Se vuelve de la de arriba, como con su botón; si el «atrás» saltó
    //  varios pasos, de las que hagan falta.
    while (PILA.length > en) volverDe(PILA[PILA.length - 1].el);
  } else PASOS = en;
  ajustarHistorial();
});

//  Red de seguridad: si una pantalla esconde su ficha por su cuenta (con
//  `oculto`, sin pasar por acá), la pestaña no puede quedar en blanco.
new MutationObserver(() => {
  const f = PILA[PILA.length - 1];
  if (f && f.el.classList.contains('oculto')) volverDe(f.el);
}).observe(document.querySelector('main') || document.body,
  { subtree: true, attributes: true, attributeFilter: ['class'] });

// ---------------------------------------------------------------------
//  2 · Plegar las tarjetas
// ---------------------------------------------------------------------

const CLAVE = 'nubilon.plegadas';
let PLEGADAS: Record<string, true> = {};
try { PLEGADAS = JSON.parse(localStorage.getItem(CLAVE) || '{}') || {}; } catch { PLEGADAS = {}; }
function guardar(){
  try { localStorage.setItem(CLAVE, JSON.stringify(PLEGADAS)); } catch { /* sin guardar: igual pliega */ }
}

//  El título de la tarjeta: el hijo directo que tiene su primer `h2`.
function cabeceraDe(card: HTMLElement): HTMLElement | null {
  const h2 = card.querySelector('h2');
  if (!h2) return null;
  let x: HTMLElement = h2;
  while (x.parentElement && x.parentElement !== card) x = x.parentElement;
  return x.parentElement === card ? x : null;
}

const tituloDe = (card: HTMLElement) =>
  (card.querySelector('h2')?.textContent || 'la tarjeta').replace(/\s+/g, ' ').trim();

function ponerPlegada(card: HTMLElement, plegada: boolean, recordar = true){
  card.classList.toggle('plegada', plegada);
  const b = card.querySelector(':scope > .card-plegar') as HTMLElement | null;
  if (b){
    b.setAttribute('aria-expanded', String(!plegada));
    b.setAttribute('aria-label', (plegada ? 'Desplegar «' : 'Plegar «') + tituloDe(card) + '»');
    b.title = plegada ? 'Desplegar' : 'Plegar';
  }
  if (recordar && card.dataset.plegarClave){
    if (plegada) PLEGADAS[card.dataset.plegarClave] = true;
    else delete PLEGADAS[card.dataset.plegarClave];
    guardar();
  }
  pintarBotonesTodas();
}

function armarTarjeta(card: HTMLElement, clave: string){
  if (card.dataset.plegarClave) return;
  const cab = cabeceraDe(card);
  if (!cab) return;
  card.dataset.plegarClave = clave;
  card.classList.add('plegable');
  cab.classList.add('card-cab');
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'card-plegar';
  card.insertBefore(b, card.firstChild);
  b.addEventListener('click', () => ponerPlegada(card, !card.classList.contains('plegada')));
  //  Tocar el título también pliega, salvo que se toque algo que hace otra
  //  cosa (un botón, un enlace, un campo).
  cab.addEventListener('click', e => {
    if ((e.target as Element).closest('button, a, input, select, textarea, label, summary')) return;
    if (card.classList.contains('en-ficha')) return;
    ponerPlegada(card, !card.classList.contains('plegada'));
  });
  ponerPlegada(card, !!PLEGADAS[clave], false);
}

//  «Plegar todas» / «Desplegar todas», arriba de cada pantalla con dos
//  tarjetas plegables o más. Cuentan sólo las que se ven: las de un permiso
//  que no tenés no existen para vos.
const visibles = (s: HTMLElement) => Array.from(s.querySelectorAll<HTMLElement>('.card.plegable'))
  .filter(c => !c.closest('.oculto') && !c.classList.contains('oculto'));

function pintarBotonesTodas(){
  document.querySelectorAll<HTMLElement>('main > section > .plegar-todas').forEach(barra => {
    const s = barra.parentElement as HTMLElement;
    const cs = visibles(s);
    barra.classList.toggle('oculto', cs.length < 2);
    const b = barra.querySelector('button') as HTMLElement;
    const alguna = cs.some(c => !c.classList.contains('plegada'));
    const texto = alguna ? 'Plegar todas' : 'Desplegar todas';
    if (b.textContent !== texto) b.textContent = texto;
  });
}

function armarSeccion(s: HTMLElement){
  const cards = Array.from(s.querySelectorAll<HTMLElement>('.card'));
  cards.forEach((c, i) => armarTarjeta(c, `${s.id}/${c.id || 'n' + i}`));
  if (s.querySelectorAll('.card.plegable').length < 2) return;
  const barra = document.createElement('div');
  barra.className = 'plegar-todas oculto';
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'b b-sec b-chico';
  barra.appendChild(b);
  s.insertBefore(barra, s.firstChild);
  b.addEventListener('click', () => {
    const cs = visibles(s);
    const plegar = cs.some(c => !c.classList.contains('plegada'));
    cs.forEach(c => ponerPlegada(c, plegar));
  });
}

//  Un aviso o un error que llega a una tarjeta plegada la despliega (sin
//  recordarlo: la próxima vez arranca como la dejó la persona).
function desplegarSiHayAviso(n: Node){
  if (!(n instanceof Element)) return;
  const avisos = n.matches('.a-bad, .a-warn') ? [n] : Array.from(n.querySelectorAll('.a-bad, .a-warn'));
  for (const a of avisos){
    const c = a.closest('.card.plegada') as HTMLElement | null;
    if (c) ponerPlegada(c, false, false);
  }
}

export function armarNavegacion(){
  document.querySelectorAll<HTMLElement>('main > section').forEach(armarSeccion);
  pintarBotonesTodas();
  let pendiente = false;
  new MutationObserver(cambios => {
    for (const c of cambios) if (c.type === 'childList') c.addedNodes.forEach(desplegarSiHayAviso);
    //  Lo que se ve cambia con los permisos y las pestañas: el botón de
    //  todas se repinta una vez por tanda.
    if (pendiente) return;
    pendiente = true;
    queueMicrotask(() => { pendiente = false; pintarBotonesTodas(); });
  }).observe(document.querySelector('main') as HTMLElement,
    { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
}
